/**
 * Service for loading level and solution data.
 *
 * Isolates all dynamic require() calls to enable testability through dependency injection.
 * Production code uses ProductionLevelDataService which performs actual file loading.
 * Tests use MockLevelDataService to inject controlled test data.
 */

export interface LevelDataService {
  /**
   * Load level data JSON for the given level ID.
   * @param levelId The level identifier (e.g., "level-1-1")
   * @returns The level data object
   */
  getLevelData(levelId: string): any;

  /**
   * Load solution data JSON for the given level ID.
   * @param levelId The level identifier (e.g., "level-1-1")
   * @returns The solution data object, or null if no solution exists
   */
  getSolutionData(levelId: string): any | null;
}

/**
 * Production implementation that loads actual JSON files from the content directory.
 */
export class ProductionLevelDataService implements LevelDataService {
  getLevelData(levelId: string): any {
    const levelModule = require(`../../../content/dist/levels/${levelId}.json`);
    return levelModule.default || levelModule;
  }

  getSolutionData(levelId: string): any | null {
    try {
      const solutionModule = require(`../../../content/dist/levels/${levelId}.solution.json`);
      return solutionModule.default || solutionModule;
    } catch (e) {
      // Solution file doesn't exist for this level
      return null;
    }
  }
}

/**
 * Mock implementation for testing.
 * Allows tests to inject controlled data without file system dependencies.
 */
export class MockLevelDataService implements LevelDataService {
  private levels: Map<string, any> = new Map();
  private solutions: Map<string, any> = new Map();

  getLevelData(levelId: string): any {
    if (!this.levels.has(levelId)) {
      throw new Error(`Mock level not found: ${levelId}`);
    }
    return this.levels.get(levelId);
  }

  getSolutionData(levelId: string): any | null {
    return this.solutions.get(levelId) || null;
  }

  /**
   * Test helper to register level data.
   */
  setLevelData(levelId: string, data: any): void {
    this.levels.set(levelId, data);
  }

  /**
   * Test helper to register solution data.
   */
  setSolutionData(levelId: string, data: any): void {
    this.solutions.set(levelId, data);
  }
}
